import { useState, useEffect, useCallback, useMemo } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { makeSearchHelpers } from "utilities/searchDynamic";

// SOUND-688: filter params are DERIVED from searchConfig via `apiName`, not
// hand-listed here.
//
// The hand-written allowlist this replaces silently dropped any filter added to
// the config but not also added here — and it dropped it TWICE, once from the
// query string and once from the useCallback deps, so the toggle did not even
// trigger a refetch. `hasPends` shipped in the config, rendered its control, wrote
// to the URL, and did nothing at all.
//
// `pruneDefaults` keeps a filter sitting at its declared default out of the
// query string, which is what the old `if (filters.x)` guards were doing by hand.
const appSearch = makeSearchHelpers("midApplications");

export function useMidApplications({
  filters = {},
  page = 1,
  limit = 25,
  sort = "-updatedAt",
} = {}) {
  const [applications, setApplications] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  // SOUND-295: aggregate counts over the FILTERED set, computed server-side.
  // KPI cards must never be derived from the current page.
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  const filterParams = useMemo(
    () => JSON.stringify(appSearch.toApiParams(filters, { pruneDefaults: true })),
    [filters]
  );

  const fetchApplications = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      params.set("sort", sort);

      for (const [k, v] of Object.entries(JSON.parse(filterParams))) {
        params.set(k, String(v));
      }

      const response = await makeApiRequest(
        "get",
        `${apiRoutes.listMidApplications}?${params.toString()}`,
        { id: auth?.user?.id, accessToken: auth?.user?.accessToken },
        companyId
      );

      const body = response?.data || {};
      setApplications(Array.isArray(body.data) ? body.data : []);
      setPagination(body.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 });
      setSummary(body.summary || {});
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
    // `filterParams` is a STRING, so it is stable by value. Depending on the
    // `filters` object instead would refetch on every render that produces a new
    // object identity, and listing each field by hand is the bug above.
  }, [companyId, page, limit, sort, filterParams]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  return { applications, pagination, summary, loading, error, refetch: fetchApplications };
}
